/**
 * Hằng số dùng riêng cho module Hoa hồng V2 (commission).
 * Giá trị enum khớp với backend — xem SRS-DCore-Reward-Refactor §3.2, §4.3, §4.12.
 */

/** Loại nhóm hoa hồng — CommissionCategories.type */
export const COMMISSION_CATEGORY_TYPE = Object.freeze({
  PRODUCT: 1, // nhóm sản phẩm / danh mục
  TRADE_IN: 2, // nhóm thưởng thu cũ đổi mới
  SHIPPING: 3, // nhóm thưởng đơn giao hàng
});

export const COMMISSION_CATEGORY_TYPE_NAME = Object.freeze({
  [COMMISSION_CATEGORY_TYPE.PRODUCT]: 'Sản phẩm / Danh mục',
  [COMMISSION_CATEGORY_TYPE.TRADE_IN]: 'Thu cũ đổi mới',
  [COMMISSION_CATEGORY_TYPE.SHIPPING]: 'Đơn giao hàng',
});

/** Loại item trong nhóm — CommissionCategoryDetails.type */
export const COMMISSION_CATEGORY_ITEM_TYPE = Object.freeze({
  PRODUCT: 1, // referenceId trỏ Products.id
  CATEGORY: 2, // referenceId trỏ Categories.id
});

export const COMMISSION_CATEGORY_ITEM_TYPE_NAME = Object.freeze({
  [COMMISSION_CATEGORY_ITEM_TYPE.PRODUCT]: 'Sản phẩm',
  [COMMISSION_CATEGORY_ITEM_TYPE.CATEGORY]: 'Danh mục',
});

/** Cách tìm khi thêm item lẻ — chỉ là bộ lọc lúc tìm, xem §3.6 */
export const COMMISSION_SEARCH_TYPE = Object.freeze({
  PRODUCT_IMEI: 1,
  PRODUCT: 2,
  CATEGORY: 3,
});

/** Loại chương trình — CommissionPrograms.type */
export const COMMISSION_PROGRAM_TYPE = Object.freeze({
  STORE: 1, // ngành cửa hàng
  CR: 3, // chăm sóc khách hàng
});

export const COMMISSION_PROGRAM_TYPE_NAME = Object.freeze({
  [COMMISSION_PROGRAM_TYPE.STORE]: 'Chương trình hoa hồng cửa hàng',
  [COMMISSION_PROGRAM_TYPE.CR]: 'Chương trình hoa hồng CR',
});

/** Trạng thái chương trình — §4.12 */
// Chỉ 2 trạng thái: hoạt động / ngưng. Giữ nguyên giá trị 2 và 3 khớp API.
export const COMMISSION_PROGRAM_STATUS = Object.freeze({
  ACTIVE: 2,
  INACTIVE: 3,
});

export const COMMISSION_PROGRAM_STATUS_NAME = Object.freeze({
  [COMMISSION_PROGRAM_STATUS.ACTIVE]: 'Hoạt động',
  [COMMISSION_PROGRAM_STATUS.INACTIVE]: 'Ngưng hoạt động',
});

/** Class badge tương ứng trạng thái */
export const COMMISSION_PROGRAM_STATUS_VARIANT = Object.freeze({
  [COMMISSION_PROGRAM_STATUS.ACTIVE]: 'label-light-success',
  [COMMISSION_PROGRAM_STATUS.INACTIVE]: 'label-light-danger',
});

/** Phạm vi cửa hàng — CommissionProgramStores.scope */
export const COMMISSION_STORE_SCOPE = Object.freeze({
  RETAIL: 1, // bán lẻ
  WHOLESALE: 2, // bán buôn
});

export const COMMISSION_STORE_SCOPE_NAME = Object.freeze({
  [COMMISSION_STORE_SCOPE.RETAIL]: 'Bán lẻ',
  [COMMISSION_STORE_SCOPE.WHOLESALE]: 'Bán buôn',
});

/** Hành động ghi lịch sử */
export const COMMISSION_HISTORY_ACTION_NAME = Object.freeze({
  1: 'Tạo mới',
  2: 'Chỉnh sửa',
  3: 'Xóa',
});

/** Nhãn toán tử hiển thị — giá trị khớp OPERATOR_TYPE trong @/utils/enum */
export const COMMISSION_OPERATOR_OPTIONS = Object.freeze([
  { value: 1, text: '--' },
  { value: 2, text: '<' },
  { value: 3, text: '>' },
  { value: 4, text: '≤' },
  { value: 5, text: '≥' },
]);

/** Số dòng tối đa cho mỗi lần nhập Excel — §3.8 quy tắc 5 */
export const COMMISSION_IMPORT_MAX_ROWS = 1000;

/* -------------------------------------------------------------------------- */
/* F-03 — Cộng trừ khác hoa hồng cửa hàng (§5.4)                              */
/* -------------------------------------------------------------------------- */

/** Toán tử cộng/trừ — CommissionAdjustments.operatorType */
export const COMMISSION_ADJUSTMENT_OPERATOR = Object.freeze({
  BONUS: 1, // cộng
  MINUS: 2, // trừ
});

export const COMMISSION_ADJUSTMENT_OPERATOR_NAME = Object.freeze({
  [COMMISSION_ADJUSTMENT_OPERATOR.BONUS]: 'Cộng (+)',
  [COMMISSION_ADJUSTMENT_OPERATOR.MINUS]: 'Trừ (−)',
});

/** Ký hiệu ngắn hiển thị trong bảng danh sách */
export const COMMISSION_ADJUSTMENT_OPERATOR_SIGN = Object.freeze({
  [COMMISSION_ADJUSTMENT_OPERATOR.BONUS]: '+',
  [COMMISSION_ADJUSTMENT_OPERATOR.MINUS]: '−',
});

/** Đơn vị giá trị — CommissionAdjustments.amountType */
export const COMMISSION_ADJUSTMENT_AMOUNT_TYPE = Object.freeze({
  AMOUNT: 1, // tiền
  PERCENT: 2, // phần trăm trên tổng điểm hoa hồng của kỳ
});

export const COMMISSION_ADJUSTMENT_AMOUNT_TYPE_NAME = Object.freeze({
  [COMMISSION_ADJUSTMENT_AMOUNT_TYPE.AMOUNT]: 'Tiền ($)',
  [COMMISSION_ADJUSTMENT_AMOUNT_TYPE.PERCENT]: 'Phần trăm (%)',
});

export const COMMISSION_ADJUSTMENT_AMOUNT_TYPE_SIGN = Object.freeze({
  [COMMISSION_ADJUSTMENT_AMOUNT_TYPE.AMOUNT]: '$',
  [COMMISSION_ADJUSTMENT_AMOUNT_TYPE.PERCENT]: '%',
});

/** Nguồn phát sinh — ảnh hưởng cắt phụ cấp lương (§5.2) */
export const COMMISSION_ADJUSTMENT_SOURCE = Object.freeze({
  OTHER: 1, // khác
  CSKH: 2, // chăm sóc khách hàng
  QA: 3, // kiểm định chất lượng
});

export const COMMISSION_ADJUSTMENT_SOURCE_NAME = Object.freeze({
  [COMMISSION_ADJUSTMENT_SOURCE.OTHER]: 'Khác',
  [COMMISSION_ADJUSTMENT_SOURCE.CSKH]: 'CSKH',
  [COMMISSION_ADJUSTMENT_SOURCE.QA]: 'QA',
});

/** Nhóm lý do — ảnh hưởng cắt phụ cấp lương (§5.2) */
export const COMMISSION_ADJUSTMENT_REASON = Object.freeze({
  PERSONAL_APPEARANCE: 1, // lỗi cá nhân - tác phong đẹp
  PERSONAL_OTHER: 2, // lỗi cá nhân - khác
  STORE_IMAGE: 3, // lỗi cửa hàng - hình ảnh cửa hàng
  STORE_OTHER: 4, // lỗi cửa hàng - khác
});

export const COMMISSION_ADJUSTMENT_REASON_NAME = Object.freeze({
  [COMMISSION_ADJUSTMENT_REASON.PERSONAL_APPEARANCE]:
    'Lỗi cá nhân - tác phong đẹp',
  [COMMISSION_ADJUSTMENT_REASON.PERSONAL_OTHER]: 'Lỗi cá nhân - khác',
  [COMMISSION_ADJUSTMENT_REASON.STORE_IMAGE]:
    'Lỗi Cửa hàng - Hình ảnh cửa hàng',
  [COMMISSION_ADJUSTMENT_REASON.STORE_OTHER]: 'Lỗi Cửa hàng Khác',
});

/** Mức đánh giá — chỉ dùng khi tạo theo hóa đơn / theo ngày (§5.7) */
export const COMMISSION_ADJUSTMENT_RATING = Object.freeze({
  PRAISE: 1, // khen
  SATISFIED: 2, // hài lòng
  NOT_SATISFIED: 3, // không hài lòng
});

export const COMMISSION_ADJUSTMENT_RATING_NAME = Object.freeze({
  [COMMISSION_ADJUSTMENT_RATING.PRAISE]: 'Khen',
  [COMMISSION_ADJUSTMENT_RATING.SATISFIED]: 'Hài lòng',
  [COMMISSION_ADJUSTMENT_RATING.NOT_SATISFIED]: 'Không hài lòng',
});

/**
 * §5.7 — Ánh xạ Mức đánh giá -> Toán tử cộng/trừ.
 * CẤM hard-code quan hệ này ở bất kỳ chỗ nào khác (không viết `rating === 3 ? 2 : 1`).
 * Mọi nơi cần suy ra toán tử từ mức đánh giá đều phải gọi `operatorFromRating()`.
 * Thêm mức đánh giá mới = thêm 1 dòng ở đây, không sửa logic.
 */
export const COMMISSION_RATING_TO_OPERATOR = Object.freeze({
  [COMMISSION_ADJUSTMENT_RATING.PRAISE]: COMMISSION_ADJUSTMENT_OPERATOR.BONUS,
  [COMMISSION_ADJUSTMENT_RATING.SATISFIED]:
    COMMISSION_ADJUSTMENT_OPERATOR.BONUS,
  [COMMISSION_ADJUSTMENT_RATING.NOT_SATISFIED]:
    COMMISSION_ADJUSTMENT_OPERATOR.MINUS,
});

/**
 * Tra toán tử gợi ý từ mức đánh giá (§5.7).
 * Trả về null nếu chưa chọn mức đánh giá — người dùng vẫn tự chọn toán tử được.
 */
export const operatorFromRating = (ratingType) => {
  const rating = Number(ratingType);
  return COMMISSION_RATING_TO_OPERATOR[rating] || null;
};

/** Bội số làm tròn xuống khi chia đều giá trị nguồn QA (§5.6) */
export const COMMISSION_ADJUSTMENT_ROUND_STEP = 100;

/** Số dòng tối đa mỗi lần nhập Excel bút toán — khớp MAX_IMPORT_ROWS của BE */
export const COMMISSION_ADJUSTMENT_IMPORT_MAX_ROWS = 500;

export const toOptionList = (nameMap) =>
  Object.keys(nameMap).map((key) => ({
    id: Number(key),
    name: nameMap[key],
  }));

/* -------------------------------------------------------------------------- */
/* F-10 — Báo cáo hoa hồng (§7)                                               */
/* -------------------------------------------------------------------------- */

/**
 * Loại báo cáo — tham số `type` của GET /api/commission-reports (§7.5).
 * Chú ý: đây là CHUỖI, khác với COMMISSION_PROGRAM_TYPE (số).
 */
export const COMMISSION_REPORT_TYPE = Object.freeze({
  STORE: 'store', // ngành cửa hàng — lọc theo storeId
  CR: 'cr', // chăm sóc khách hàng — lọc theo departmentId
});

export const COMMISSION_REPORT_TYPE_NAME = Object.freeze({
  [COMMISSION_REPORT_TYPE.STORE]: 'Báo cáo hoa hồng cửa hàng',
  [COMMISSION_REPORT_TYPE.CR]: 'Báo cáo hoa hồng CR',
});

/** Nguồn phát sinh dòng hoa hồng — billRows[].sourceType / adjustments[].sourceType */
export const COMMISSION_SOURCE_TYPE = Object.freeze({
  SALE: 1, // bán hàng
  RETURN: 2, // trả hàng
  SHIPPING: 3, // giao hàng
  TRADE_IN: 4, // thu cũ đổi mới
  ADHOC: 5, // thưởng AdHoc
});

export const COMMISSION_SOURCE_TYPE_NAME = Object.freeze({
  [COMMISSION_SOURCE_TYPE.SALE]: 'Bán hàng',
  [COMMISSION_SOURCE_TYPE.RETURN]: 'Trả hàng',
  [COMMISSION_SOURCE_TYPE.SHIPPING]: 'Giao hàng',
  [COMMISSION_SOURCE_TYPE.TRADE_IN]: 'Thu cũ đổi mới',
  [COMMISSION_SOURCE_TYPE.ADHOC]: 'AdHoc',
});

/** Class badge tương ứng nguồn phát sinh */
export const COMMISSION_SOURCE_TYPE_VARIANT = Object.freeze({
  [COMMISSION_SOURCE_TYPE.SALE]: 'label-light-success',
  [COMMISSION_SOURCE_TYPE.RETURN]: 'label-light-danger',
  [COMMISSION_SOURCE_TYPE.SHIPPING]: 'label-light-info',
  [COMMISSION_SOURCE_TYPE.TRADE_IN]: 'label-light-warning',
  [COMMISSION_SOURCE_TYPE.ADHOC]: 'label-light-primary',
});

/**
 * Khoảng ngày tối đa của một lần xem báo cáo (§7.12 — ràng buộc giới hạn khoảng ngày).
 * BE trả 400 nếu vượt; FE chặn trước để báo lỗi rõ ràng hơn.
 */
export const MAX_REPORT_RANGE_DAYS = 92;

/** Chiều của dòng hoa hồng — billRows[].direction */
export const COMMISSION_DIRECTION = Object.freeze({
  PLUS: 1, // cộng
  MINUS: -1, // trừ (hóa đơn trả hàng)
});

/* ============================ THƯỞNG ADHOC (F-06) ============================ */

// AdHoc KHÔNG khai trạng thái riêng — dùng chung với chương trình hoa hồng
// (cùng ý nghĩa Hoạt động / Ngưng hoạt động, cùng giá trị 2 và 3).
// Giữ tên cũ làm bí danh để các màn AdHoc không phải sửa import.
export const COMMISSION_ADHOC_STATUS = COMMISSION_PROGRAM_STATUS;
export const COMMISSION_ADHOC_STATUS_NAME = COMMISSION_PROGRAM_STATUS_NAME;
export const COMMISSION_ADHOC_STATUS_VARIANT = COMMISSION_PROGRAM_STATUS_VARIANT;

/** Loại item của nhóm / dòng chi tiết AdHoc — dùng chung quy ước với nhóm hoa hồng */
export const COMMISSION_ADHOC_ITEM_TYPE = Object.freeze({
  PRODUCT: 1,
  CATEGORY: 2,
});

export const COMMISSION_ADHOC_ITEM_TYPE_NAME = Object.freeze({
  [COMMISSION_ADHOC_ITEM_TYPE.PRODUCT]: 'Sản phẩm',
  [COMMISSION_ADHOC_ITEM_TYPE.CATEGORY]: 'Danh mục',
});

/**
 * Cách ghép nhiều nhóm điều kiện của một dòng chi tiết.
 * OR  = hoá đơn có MỘT trong các nhóm là đạt (nghiệp vụ hiện tại)
 * AND = phải có ĐỦ tất cả nhóm
 */
/**
 * Cách tính khi dòng hàng VỪA có hoa hồng thường VỪA thuộc chương trình AdHoc.
 * MAX  = lấy số lớn hơn, không cộng dồn (mặc định)
 * PLUS = cộng AdHoc thêm vào hoa hồng thường
 */
export const COMMISSION_ADHOC_REWARD_MODE = Object.freeze({
  MAX: 1,
  PLUS: 2,
});

export const COMMISSION_ADHOC_REWARD_MODE_NAME = Object.freeze({
  [COMMISSION_ADHOC_REWARD_MODE.MAX]: 'Lấy số tiền lớn hơn',
  [COMMISSION_ADHOC_REWARD_MODE.PLUS]: 'Cộng thêm vào hoa hồng',
});

/** Mức dùng ngân sách của chương trình AdHoc — trùng ENUM.COMMISSION_ADHOC_BUDGET_STATUS bên API */
export const COMMISSION_ADHOC_BUDGET_STATUS = Object.freeze({
  NONE: 0,
  OK: 1,
  NEAR: 2, // từ 80% ngân sách
  OVER: 3, // từ 100% ngân sách
});

export const COMMISSION_ADHOC_BUDGET_STATUS_NAME = Object.freeze({
  [COMMISSION_ADHOC_BUDGET_STATUS.NEAR]: 'Sắp hết ngân sách',
  [COMMISSION_ADHOC_BUDGET_STATUS.OVER]: 'Vượt ngân sách',
});

export const COMMISSION_ADHOC_BUDGET_STATUS_VARIANT = Object.freeze({
  [COMMISSION_ADHOC_BUDGET_STATUS.OK]: 'success',
  [COMMISSION_ADHOC_BUDGET_STATUS.NEAR]: 'warning',
  [COMMISSION_ADHOC_BUDGET_STATUS.OVER]: 'danger',
});

export const COMMISSION_ADHOC_CONDITION_LOGIC = Object.freeze({
  OR: 1,
  AND: 2,
});

export const COMMISSION_ADHOC_CONDITION_LOGIC_NAME = Object.freeze({
  [COMMISSION_ADHOC_CONDITION_LOGIC.OR]: 'Thoả 1 nhóm là đạt (HOẶC)',
  [COMMISSION_ADHOC_CONDITION_LOGIC.AND]: 'Phải đủ tất cả nhóm (VÀ)',
});
